import React, { useEffect, useState } from "react";
import { X, CheckCircle2, AlertCircle, ExternalLink, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

/**
 * SubstackConnect — 3-step magic-link wizard that captures an authenticated
 * Substack session for a brand, so the app can auto-draft posts the user
 * publishes with one click inside Substack.
 *
 * Why magic-link paste flow (not full auto):
 *   Substack uses passwordless magic-link sign-in. We never store passwords.
 *   The user receives a sign-in email, right-clicks the button, copies the
 *   link, and pastes it here. We exchange that one-time URL for a session
 *   cookie (encrypted server-side) — same security model as logging in
 *   through their browser.
 */
export default function SubstackConnect({ brandId, brand, open, onClose, onConnected }) {
  const [status, setStatus] = useState(null);
  const [step, setStep] = useState(1); // 1=email, 2=paste link, 3=connected
  const [publicationUrl, setPublicationUrl] = useState("");
  const [loginEmail, setLoginEmail] = useState("");
  const [magicLink, setMagicLink] = useState("");
  const [busy, setBusy] = useState(false);
  const [requiresUserAction, setRequiresUserAction] = useState(null);

  useEffect(() => {
    if (!open || !brandId) return;
    (async () => {
      try {
        const s = await api.substackStatus(brandId);
        setStatus(s);
        setPublicationUrl(s.publication_url || brand?.substack_publication_url || "");
        setLoginEmail(s.login_email || brand?.substack_login_email || "");
        setStep(s.connected ? 3 : 1);
      } catch (_) {}
    })();
  }, [open, brandId, brand]);

  if (!open) return null;

  const isValidUrl = (u) => /^https?:\/\/[^\s]+\.substack\.com\/?$/i.test(u.trim());
  const isValidEmail = (e) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(e.trim());

  const sendLink = async () => {
    if (!isValidEmail(loginEmail)) return toast.error("Enter a valid email");
    if (!isValidUrl(publicationUrl)) return toast.error("Enter your full publication URL, e.g. https://yourbrand.substack.com");
    setBusy(true);
    try {
      const r = await api.substackRequestLink(brandId, loginEmail.trim(), publicationUrl.trim());
      if (r.sent) {
        toast.success(`Magic link sent to ${loginEmail}. Check your inbox.`);
      } else {
        setRequiresUserAction(r);
        toast.message("Substack needs you to trigger the link from their site (captcha).", { duration: 6000 });
      }
      setStep(2);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not request magic link");
    } finally {
      setBusy(false);
    }
  };

  const exchange = async () => {
    if (!magicLink.trim()) return toast.error("Paste the magic-link URL from your email first");
    setBusy(true);
    try {
      const r = await api.substackExchange(brandId, {
        login_email: loginEmail.trim(),
        publication_url: publicationUrl.trim(),
        magic_link_url: magicLink.trim(),
      });
      setStatus(r);
      setStep(3);
      toast.success("Substack connected. Drafts will be ready to publish in one click.");
      setMagicLink("");
      onConnected && onConnected(r);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Magic-link exchange failed");
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect this brand from Substack? Drafts will stop auto-creating.")) return;
    setBusy(true);
    try {
      await api.substackDisconnect(brandId);
      setStatus({ connected: false });
      setStep(1);
      toast.success("Disconnected");
    } catch (e) {
      toast.error("Could not disconnect");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
         data-testid="substack-connect-modal"
         onClick={onClose}>
      <div className="bg-white border-2 border-black w-full max-w-2xl p-8 brutal-shadow-lg max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-eyebrow text-[#A1A1AA]">Substack auto-draft</div>
          <button onClick={onClose} data-testid="substack-connect-close" aria-label="Close"><X size={20} /></button>
        </div>
        <h2 className="font-display font-black text-3xl tracking-tighter mb-1">Connect Substack</h2>
        <p className="text-sm text-[#52525B] mb-6">
          Every approved post auto-creates a <strong>draft</strong> in your Substack publication.
          You click <em>Publish</em> there. No bot detection, no flagging.
        </p>

        {step === 3 && status?.connected && (
          <div data-testid="substack-connected-panel" className="space-y-4">
            <div className="border border-[#10B981] bg-[#ECFDF5] p-4 flex items-start gap-3">
              <CheckCircle2 size={20} className="text-[#10B981] mt-0.5" />
              <div className="flex-1 text-sm">
                <div className="font-semibold">Connected</div>
                <div className="text-[#52525B] mt-1 break-all">{status.publication_url}</div>
                {status.login_email && <div className="text-xs text-[#A1A1AA] mt-1">{status.login_email}</div>}
                {status.captured_at && <div className="text-xs text-[#A1A1AA] mt-1">Session captured {new Date(status.captured_at).toLocaleString()}</div>}
              </div>
            </div>
            {status.last_error && (
              <div className="border border-[#F59E0B] bg-[#FFF7ED] p-3 text-xs flex items-start gap-2">
                <AlertCircle size={16} className="text-[#F59E0B] mt-0.5" />
                <div>Last issue: {String(status.last_error)}. If drafts stop working, reconnect.</div>
              </div>
            )}
            <div className="flex gap-3 justify-end">
              <button onClick={disconnect} disabled={busy} data-testid="substack-disconnect-btn"
                      className="px-4 py-2 text-sm border border-[#0A0A0B] hover:bg-[#F4F5F6]">Disconnect</button>
              <button onClick={() => { setStep(1); setMagicLink(""); }} data-testid="substack-reconnect-btn"
                      className="px-4 py-2 text-sm bg-[#0A0A0B] text-white hover:bg-[#002FA7]">Re-link session</button>
            </div>
          </div>
        )}

        {step === 1 && (
          <div data-testid="substack-step-1" className="space-y-4">
            <ol className="space-y-1 text-xs text-[#52525B] list-decimal list-inside mb-2">
              <li>Enter the email you use to log into Substack</li>
              <li>Enter your publication URL</li>
              <li>We'll trigger Substack's magic-link → you paste it back to finish</li>
            </ol>
            <div>
              <label className="text-eyebrow block mb-2">Substack login email</label>
              <input
                type="email"
                value={loginEmail}
                onChange={(e) => setLoginEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="substack-login-email"
                className="w-full border border-[#E2E4E9] focus:border-black p-3 outline-none"
              />
            </div>
            <div>
              <label className="text-eyebrow block mb-2">Publication URL</label>
              <input
                type="url"
                value={publicationUrl}
                onChange={(e) => setPublicationUrl(e.target.value)}
                placeholder="https://yourbrand.substack.com"
                data-testid="substack-publication-url"
                className="w-full border border-[#E2E4E9] focus:border-black p-3 outline-none"
              />
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={onClose} className="px-4 py-2 text-sm border border-black hover:bg-[#F4F5F6]">Cancel</button>
              <button
                onClick={sendLink}
                disabled={busy}
                data-testid="substack-send-link-btn"
                className="px-5 py-3 text-sm bg-[#0A0A0B] text-white hover:bg-[#002FA7] inline-flex items-center gap-2"
              >
                {busy && <Loader2 size={16} className="animate-spin" />}
                Send me the magic link
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="substack-step-2" className="space-y-4">
            {requiresUserAction ? (
              <div className="border-l-4 border-[#F59E0B] bg-[#FFF7ED] p-3 text-sm">
                Substack required a captcha to send the link, so we couldn't trigger it from here.
                <div className="mt-2">
                  Click below, sign in with <strong>{loginEmail}</strong>, then come back and paste the link from your inbox.
                </div>
                <a
                  href={requiresUserAction.sign_in_url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 underline mt-2"
                  data-testid="substack-signin-link"
                >
                  Open Substack sign-in <ExternalLink size={14} />
                </a>
              </div>
            ) : (
              <div className="border-l-4 border-[#002FA7] bg-[#EFF6FF] p-3 text-sm">
                Open the email from Substack. <strong>Right-click</strong> the "Sign in" button and pick <em>Copy link address</em>. Then paste it below.
              </div>
            )}
            <div>
              <label className="text-eyebrow block mb-2">Magic-link URL from your email</label>
              <textarea
                value={magicLink}
                onChange={(e) => setMagicLink(e.target.value)}
                rows={3}
                placeholder="https://substack.com/email-action/..."
                data-testid="substack-magic-link"
                className="w-full border border-[#E2E4E9] focus:border-black p-3 outline-none mono text-xs"
              />
              <p className="text-xs text-[#A1A1AA] mt-1">
                We don't store this URL. We exchange it once for a session cookie, encrypt it,
                and use it only to create drafts. You can disconnect anytime.
              </p>
            </div>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setStep(1)} className="px-4 py-2 text-sm border border-black hover:bg-[#F4F5F6]">Back</button>
              <button
                onClick={exchange}
                disabled={busy}
                data-testid="substack-exchange-btn"
                className="px-5 py-3 text-sm bg-[#0A0A0B] text-white hover:bg-[#002FA7] inline-flex items-center gap-2"
              >
                {busy && <Loader2 size={16} className="animate-spin" />}
                Finish connection
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
